import { SeverityIcon } from '@/ui/components/svg/SeverityIcon'
import { FilterModuleConfig, transformToGenericFilterConfig } from '../filters'
import { createElement } from 'react'
import { UnifiedFilterConfig, SearchKeyConfig } from '@core/modules/filters/unified-filter.module'
import { EIssueSeverity } from './constants'

export const issuesSearchKeys: SearchKeyConfig[] = [
    { value: 'name', threshold: 0.3 },
    { value: 'short_description', threshold: 0.4 },
    { value: 'severity', threshold: 0.2 },
    { value: 'issue_category', threshold: 0.2 },
]

const config = {
    severity: {
        accessor: 'severity',
        label: 'Severity',
        collapsible: true,
        withSearch: false,
        filters: {
            critical: {
                accessor: 'critical',
                label: 'Critical',
                icon: createElement(SeverityIcon, {
                    severity: 'critical',
                }),
            },
            important: {
                accessor: 'important',
                label: 'Important',
                icon: createElement(SeverityIcon, {
                    severity: 'important',
                }),
            },
            moderate: {
                accessor: 'moderate',
                label: 'Moderate',
                icon: createElement(SeverityIcon, {
                    severity: 'moderate',
                }),
            },
        },
    },
    issue_category: {
        accessor: 'issue_category',
        label: 'Issue type',
        collapsible: true,
        withSearch: false,
        filters: {
            email_threats: {
                accessor: 'email_threats',
                label: 'Email threats',
            },
            configuration_changes: {
                accessor: 'configuration_changes',
                label: 'Configuration changes',
            },
            software_updates: {
                accessor: 'software_updates',
                label: 'Software updates',
            },
            network_exposures: {
                accessor: 'network_exposures',
                label: 'Network exposure',
            },
            security_controls: {
                accessor: 'security_controls',
                label: 'Security controls',
            },
            user_account_compromise: {
                accessor: 'user_account_compromise',
                label: 'User account compromise',
            },
        },
    },
    status: {
        accessor: 'status',
        label: 'Status',
        collapsible: true,
        withSearch: false,
        filters: {
            open: {
                accessor: 'open',
                label: 'Open',
            },
            resolved: {
                accessor: 'resolved',
                label: 'Resolved',
            },
            approved: {
                accessor: 'approved',
                label: 'Approved',
            },
            in_review: {
                accessor: 'in_review',
                label: 'Validating',
            },
        },
    },
}

const defaultFilters = {
    severity: {
        critical: true,
        important: true,
        moderate: true,
    },
    issue_category: {
        email_threats: true,
        configuration_changes: true,
        software_updates: true,
        network_exposures: true,
        security_controls: true,
        user_account_compromise: true,
    },
    status: {
        open: true,
        resolved: false,
        approved: false,
        in_review: true,
    },
}

export const issuesFilterConfig: FilterModuleConfig =
    transformToGenericFilterConfig(
        { issues: config },
        { issues: defaultFilters }
    ).issues

const sortBySeverity = (a: TIssue, b: TIssue): number | null => {
    const aSeverity = EIssueSeverity[a.severity]
    const bSeverity = EIssueSeverity[b.severity]
    if (aSeverity !== bSeverity) {
        return bSeverity - aSeverity
    }
    return null
}

const sortByDate = (a: TIssue, b: TIssue): number | null => {
    const aDate = new Date(a.date_sent || '').getTime()
    const bDate = new Date(b.date_sent || '').getTime()
    if (aDate !== bDate) {
        return bDate - aDate
    }
    return null
}

const sortByName = (a: TIssue, b: TIssue): number | null => {
    return a.name.localeCompare(b.name)
}

export const unifiedIssuesConfig: UnifiedFilterConfig = {
    ...issuesFilterConfig,
    searchKeys: issuesSearchKeys,
    sortStrategy: (a: TIssue, b: TIssue) => {
        const severityComparison = sortBySeverity(a, b)
        if (severityComparison !== null) {
            return severityComparison
        }

        const nameComparison = sortByName(a, b)
        if (nameComparison !== null) {
            return nameComparison
        }

        const dateComparison = sortByDate(a, b)
        if (dateComparison !== null) {
            return dateComparison
        }
        return a.name.localeCompare(b.name)
    },
}

export { config, defaultFilters }
